/**
 * FFH-1849: internal flow pages that must never be recorded as a checkout
 * entry point — neither as the checkout-initiating page (`converted_page_url`,
 * see initiating-page.ts) nor the first landing page (`first_landing_page`,
 * see ../attribution/first-landing-page.ts). A reload or a direct hit on one
 * of these must not overwrite or seed the real entry page with a
 * non-actionable internal route.
 */
export const EXCLUDED_ENTRY_PREFIXES = [
  '/checkout-stripe',
  '/thank-you',
  '/funnel-welcome',
  '/auth'
];

/**
 * Paywall and account pages. These are excluded from `converted_page_url` ONLY.
 *
 * `converted_page_url` answers "which page drove the signup", and a paywall
 * never does — almost every buyer passes through /join immediately before
 * Stripe, so letting it record swamps the field with the paywall instead of
 * the content or tool page that actually did the persuading. Tool pages
 * (/my-team/*) are deliberately absent from this list: those are genuine
 * last touches.
 *
 * They are NOT excluded from `first_landing_page`, which answers a different
 * question — "where did this visit start". A paid ad pointing straight at
 * /join is a real entry, and the one signal paid attribution has for that
 * journey. Excluding it there would delete data rather than clean it.
 */
export const EXCLUDED_INITIATING_PREFIXES = [
  ...EXCLUDED_ENTRY_PREFIXES,
  '/join',
  '/member-upgrade',
  '/account'
];

/**
 * Whole-segment match: '/auth' matches '/auth' and '/auth/login' but NOT
 * '/author/[slug]', which merely shares the string prefix.
 */
export const matchesSegment = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isExcludedEntryPath = (pathname: string): boolean =>
  EXCLUDED_ENTRY_PREFIXES.some((prefix) => matchesSegment(pathname, prefix));

export const isExcludedInitiatingPath = (pathname: string): boolean =>
  EXCLUDED_INITIATING_PREFIXES.some((prefix) =>
    matchesSegment(pathname, prefix)
  );
